












































import Vue from 'vue';
import {
  Component, Inject, Watch, Prop,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';
import { Product, ProductResource } from '@/api/product';
import QualRewardGroupPanel from '@/components/products/QualRewardGroupPanel.vue';
import { QualRewardGroup } from '@/api/product/levelrewards';
import { InterestReward } from '@/api/product/levelrewards/rewards';
import { ProductTypesResource, ProductTypeMap } from '@/api/producttypes';
import { AccountProductTypesResource, AccountProductTypeMap } from '@/api/accountproducttypes';
import { ProductMappingResource, ProductMapping } from '@/api/productmapping';
import { ATM, ATMSettingsResource } from '@/api/atmsettings';
import { DateTool } from '@/util';

// Need to give groups a unique id for list
interface QualRewardGroupEntry {
  id: number,
  group: QualRewardGroup,
}

@Component({
  computed: mapState(['fiId']),
  components: {
    QualRewardGroupPanel,
  },
})
export default class LevelRewards extends Vue {
  fiId!: string;

  @Prop() product!: Product;

  @Prop({ default: false }) isEditable!: boolean;

  @Prop({ default: false }) isDirty!: boolean;

  @Inject('ProductResource')
  productResource!: ProductResource;

  @Inject('ProductTypesResource')
  productTypesResource!: ProductTypesResource;

  @Inject('AccountProductTypesResource')
  accountProductTypesResource!: AccountProductTypesResource;

  @Inject('ProductMappingResource')
  productMappingResource!: ProductMappingResource;

  @Inject('ATMSettingsResource')
  atmSettingsResource!: ATMSettingsResource;

  entries: QualRewardGroupEntry[] = [];

  loading: boolean = true;

  saving: boolean = false;

  nextEntryId: number = 0;

  productHasInterestReward: boolean = false;

  productTypes!: ProductTypeMap;

  productMappings!: ProductMapping[];

  accountProductTypes!: AccountProductTypeMap;

  atmSetting!: ATM;

  mounted() {
    this.loadProductData();
    this.onProductChange();
  }

  loadProductData() {
    this.loading = true;
    const promises:Promise<any>[] = [];
    promises.push(this.productTypesResource.get());
    promises.push(this.accountProductTypesResource.get());
    promises.push(this.productMappingResource.get(this.fiId));
    promises.push(this.atmSettingsResource.getForDate(this.fiId, DateTool.todayBackendDate()));

    Promise.all(promises).then((results:any[]) => {
      this.productTypes = results[0];
      this.accountProductTypes = results[1];
      this.productMappings = results[2];
      this.atmSetting = results[3];
    }).catch((error:any) => {
  	  this.showError(error);
    }).finally(() => {
      this.loading = false;
    });
  }

  @Watch('product')
  onProductChange() {
    const tempQualRewardGroups:QualRewardGroupEntry[] = [];
    this.product.qualRewardGroups.forEach((group:QualRewardGroup) => {
      const workingGroup:QualRewardGroup = ProductResource.createEmptyQualRewardGroup();
      Object.assign(workingGroup, group);

      workingGroup.qualifiers = [];
      for (let i = 0; i < group.qualifiers.length; i++) {
        workingGroup.qualifiers.push(group.qualifiers[i].clone());
      }

      workingGroup.rewards = [];
      for (let i = 0; i < group.rewards.length; i++) {
        workingGroup.rewards.push(group.rewards[i].clone());

        /* eslint-disable no-underscore-dangle */
        if (group.rewards[i]._class === InterestReward.CLASS) {
        /* eslint-enable no-underscore-dangle */
          this.productHasInterestReward = true;
        }
      }

      tempQualRewardGroups.push({
        id: this.nextEntryId++,
        group: workingGroup,
      });
    });

    this.entries = tempQualRewardGroups.sort((first:QualRewardGroupEntry, second:QualRewardGroupEntry) : number => first.group.level - second.group.level);
  }

  onChange() {
    this.$emit('dirtyForm', true);
    this.productHasInterestReward = this.checkForInterestReward();
  }

  onReset() {
    this.$emit('reset');
  }

  checkForInterestReward() : boolean {
    for (let i = 0; i < this.entries.length; i++) {
      for (let n = 0; n < this.entries[i].group.rewards.length; n++) {
        /* eslint-disable no-underscore-dangle */
        if (this.entries[i].group.rewards[n]._class === InterestReward.CLASS) {
        /* eslint-enable no-underscore-dangle */
          return true;
        }
      }
    }

    return false;
  }

  onRewardDeleted() {
    // when removing a reward, if a level zero exists, make sure it shouldn't also be removed.
    if (this.doesLevelZeroAlreadyExist()) {
      this.removeLevelZeroIfNoInterestRewardsExist();
    }
    this.onChange();
  }

  doesLevelZeroAlreadyExist() {
    return (this.entries[0].group.level === 0);
  }

  removeLevelZeroIfNoInterestRewardsExist() {
    // if there is a level zero non-qual reward, remove it if there are no more Interest Rewards on any levels
    let interestRewardStillExists = false;
    for (let j = 1; j < this.entries.length; j++) {
      const currentQualRewardGroup = this.entries[j].group;
      for (let k = 0; k < currentQualRewardGroup.rewards.length; k++) {
        const currentReward = currentQualRewardGroup.rewards[k];
        /* eslint-disable no-underscore-dangle */
        if (currentReward._class === InterestReward.CLASS) {
        /* eslint-enable no-underscore-dangle */
          // existing Interest Reward found
          interestRewardStillExists = true;
        }
      }
    }
    if (!interestRewardStillExists) {
      // if there isn't an Interest Reward on any other level, remove level zero.
      this.entries.splice(0, 1);
    }
  }

  doAllLevelsHaveAtLeastOneReward() : string | null {
    let validationError : string | null = null;
    for (let i = 0; i < this.entries.length; i++) {
      const currentLevel:QualRewardGroup = this.entries[i].group;
      if (currentLevel.rewards.length === 0) {
        validationError = `Level ${currentLevel.level} cannot be saved without a reward configured.`;
        break;
      }
    }
    return validationError;
  }

  currentLevelRewardsConfigValid(): boolean {
    const validationErrors:string | null = this.doAllLevelsHaveAtLeastOneReward();
    if (validationErrors) {
      this.showErrorMessage(validationErrors);
      return false;
    }
    return true;
  }

  addLevel() {
    const newLevel:QualRewardGroup = ProductResource.createEmptyQualRewardGroup();

    if (this.entries.length > 0) {
      newLevel.level = this.entries[this.entries.length - 1].group.level + 1;
    } else {
      newLevel.level = 1;
    }

    this.entries.push({
      id: this.nextEntryId++,
      group: newLevel,
    });
  }

  onSave() {
    if (this.currentLevelRewardsConfigValid()) {
      this.saving = true;
      const tempProduct:Product = ProductResource.createEmptyProduct();
      Object.assign(tempProduct, this.product);

      tempProduct.qualRewardGroups = [];

      this.entries.forEach((entry:QualRewardGroupEntry) => {
        tempProduct.qualRewardGroups.push(entry.group);
      });

      this.productResource.save(this.fiId, tempProduct).then((savedProduct:Product) => {
        this.$emit('saved', savedProduct);
        this.showSuccessMessage('Product saved successfully.');
        this.saving = false;
      }).catch((error:any) => {
        this.saving = false;
  	  this.showError(error);
  	});
    }
  }

  onLevelUp(index:number) {
    if (index > 1) {
      const previousLevel:QualRewardGroupEntry = this.entries[index - 1];
      previousLevel.group.level++;
      this.entries[index].group.level--;

      // Need to use vue.set to force refresh
      Vue.set(this.entries, index - 1, this.entries[index]);
      Vue.set(this.entries, index, previousLevel);

      this.onChange();
    }
  }

  onLevelDown(index:number) {
    if (index < this.entries.length - 1) {
      const nextLevel:QualRewardGroupEntry = this.entries[index + 1];
      nextLevel.group.level--;
      this.entries[index].group.level++;

      // Need to use vue.set to force refresh
      Vue.set(this.entries, index + 1, this.entries[index]);
      Vue.set(this.entries, index, nextLevel);

      this.onChange();
    }
  }

  onLevelDelete(index:number) {
    this.entries.splice(index, 1);

    if (index < this.entries.length) {
      for (let i = index; i < this.entries.length; i++) {
        this.entries[i].group.level--;
      }
    }
    this.onChange();
  }

  onNewNonQualApr(newApr: string) {
    if (!this.doesLevelZeroAlreadyExist()) {
      const newLevel:QualRewardGroup = ProductResource.createEmptyQualRewardGroup();
      const nonQualReward = new InterestReward();
      nonQualReward.tier1.apr = newApr;
      newLevel.rewards.push(nonQualReward);

      const newEntry: QualRewardGroupEntry = {
        id: this.nextEntryId++,
        group: newLevel,
      };

      this.entries.splice(0, 0, newEntry);
    }
  }

  showError(error: any) {
	  console.error(error);
	  if (error.response && error.response.data && error.response.data.entity) {
	    this.showErrorMessage(error.response.data.entity);
	  } else if (error.response && error.response.data && Array.isArray(error.response.data.data) && error.response.data.data.length > 0) {
      this.showErrorMessage(error.response.data.data[0].message);
    } else {
	    this.showErrorMessage('An unexpected error occurred.');
	  }
  }

  showErrorMessage(error: string) {
    const al = new Alert(error, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  showSuccessMessage(error: string) {
    const al = new Alert(error, NoticeClass.SUCCESS);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
